import React, {
  memo,
  MutableRefObject,
  useEffect,
  useState,
  useRef,
  useMemo,
  useCallback,
} from "react";
import {
  BookCopy,
  LoaderCircle,
  Volume2,
  StopCircle,
  Copy as CopyIcon,
  CheckCircle as CopyCheck,
  Fullscreen,
  MonitorCheck,
  Album,
  Plus,
} from "lucide-react";
import Markdown from "react-markdown";
import { useSpeech } from "react-text-to-speech";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import remarkMath from "remark-math";
import SyntaxHighlighter from "react-syntax-highlighter";
import { tomorrow } from "react-syntax-highlighter/dist/esm/styles/hljs";
import { Message } from "../Chat/ChatWindow";
import { mergeTailwindClasses } from "../../helpers/tailwindClassHelper";
import MessageSources from "./MessageSources";
import Copy from "../MessageActions/Copy";
import { formatFileSize } from "../../helpers/fileSizeHelper";
import AnimatedProgressCircle from "./AnimatedProgressCircle";
import KyvaIcon from "../../icons/kyva";
import FileIcon from "./FileIcon";
import AttachmentSyncStatus from "../KnowledgeBase/AttachmentSyncStatus";
import {
  generateKnowledgeBasePresignedDownloadUrl,
  generateSessionFilePresignedDownloadUrl,
  getSourceDownloadUrl,
  getBrainbankSourceNames,
} from "../../service/apiService";
import { getDisplayFileName, registerOriginalName, dedupeTrailingExt } from "../../hooks/useFileUpload";
import {
  generateFeedbackSummary,
} from "../../service/apiService";
import { useSession } from "../../context/SessionContext";
import { toast } from "sonner";
import MermaidRender from "./MermaidRender";
import MermaidRenderLast from "./MermaidRenderLast";
import { v4 as uuidv4 } from "uuid";
import rehypeKatex from "rehype-katex";
import ThinkingProcess from "../Chat/ThinkingProcess";
import DocumentExport from "./DocumentExport";
import SpreadsheetExport from "./SpreadsheetExport";
import PresentationExport from "./PresentationExport";
import "katex/dist/katex.min.css";
import Documentation from "./MarkdownToNotepad";
import FilePreview from './FilePreview';
import FileOptionsMenu from './FileOptionsMenu';
import ToolOperationDisplay, { ToolOperationsProvider } from "./ToolOperationDisplay";
import WorkflowPipeline, { isWorkflowContent } from "./WorkflowPipeline";
import ClientLogo from './ClientLogo';
import { useAuth } from "../../context/AuthContext";
import { useAuth as cognitoUseAuth } from "react-oidc-context";
import { useUser } from "../../context/UserContext";
import { useCreatePermissions } from "../../hooks/useCreatePermissions";
import { formatSupportEmail, IssueData, detectError } from "../../helpers/feedbackHelper";
import { useFeedback } from "../../context/FeedbackContext";
import { useModel } from "../../context/ModelContext";
import AskKyvaButton from "../Chat/AskKyvaButton";
import QuoteBlock from "../Chat/QuoteBlock";
import QuotePreview from "../Chat/QuotePreview";

function sanitizeMath(input: string) {
  return input;
}

// BrainBank source citations cite the file's minted (ASCII-safe) stored name; the
// user's real name (emoji/unicode) lives server-side in original_file_name. We
// fetch the stored→original map once per index and feed it to registerOriginalName
// so getDisplayFileName shows the real name. Module-level so the fetch is shared
// across every message/render; a promise (not a boolean) dedupes concurrent asks.
const brainbankNameFetches = new Map<string, Promise<void>>();

const ensureBrainbankSourceNames = (indexName: string): Promise<void> => {
  let pending = brainbankNameFetches.get(indexName);
  if (!pending) {
    pending = getBrainbankSourceNames(indexName)
      .then(({ files }) => {
        (files || []).forEach((f) =>
          registerOriginalName(f.file_name, f.original_file_name)
        );
      })
      .catch((err) => {
        // Leave the fallback (suffix-stripped stored name) in place, but let a
        // later message retry rather than caching the failure forever.
        brainbankNameFetches.delete(indexName);
        console.error(`Failed to resolve brainbank source names for '${indexName}':`, err);
      });
    brainbankNameFetches.set(indexName, pending);
  }
  return pending;
};

function MessageBox({
  message,
  messageIndex,
  history,
  loading,
  dividerRef,
  isLast,
  rewrite,
  sendMessage,
  createVirtualAssistant,
  clientLogoUrl,
  logoLoading,
  logoError,
  onFilePreviewStateChange,
  handleQuote,
  highlightedQuote,
  setHighlightedQuote,
}: {
  message: Message;
  messageIndex: number;
  history: Message[];
  loading: boolean;
  dividerRef?: MutableRefObject<HTMLDivElement | null>;
  isLast: boolean;
  rewrite: (message_id: string) => void;
  sendMessage: (message: string) => void;
  createVirtualAssistant: () => void;
  clientLogoUrl: string | null;
  logoLoading: boolean;
  logoError: boolean;
  onFilePreviewStateChange?: (isOpen: boolean) => void;
  handleQuote?: (text: string, messageId: string) => void;
  highlightedQuote?: { messageId: string; text: string } | null;
  setHighlightedQuote?: (quote: { messageId: string; text: string } | null) => void;
}) {

  const [parsedMessage, setParsedMessage] = useState("");
  const [documentSegments, setDocumentSegments] = useState<{ type: 'text' | 'document' | 'spreadsheet' | 'presentation'; content: string }[]>([]);
  const [speechMessage, setSpeechMessage] = useState("");
  const [copied, setCopied] = useState(false);
  const [copiedUrl, setCopiedUrl] = useState<string | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const { sessionId } = useSession();
  const { userDisplayName } = useUser();
  const { canCreateVA } = useCreatePermissions();
  const { selectedModel } = useModel();

  const [thinkingContent, setThinkingContent] = useState("");
  // Bumped when brainbank source names resolve, to re-run the transform effect
  // below so [Source] labels update from the minted name to the real one.
  const [nameResolveVersion, setNameResolveVersion] = useState(0);

  const iframeRef = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [buttonPosition, setButtonPosition] = useState<{ x: number; y: number } | null>(null);
  const [selectedText, setSelectedText] = useState<string | null>(null);

  // Debug log for prop
  // useEffect(() => {
  //   if (highlightedQuote) {
  //      console.log("[DEBUG] MessageBox received highlightedQuote:", highlightedQuote);
  //   }
  // }, [highlightedQuote]);

  // Clear highlight after 2 seconds
  useEffect(() => {
    if (highlightedQuote && highlightedQuote.messageId === message.id) {
      const timer = setTimeout(() => {
        if (setHighlightedQuote) {
          setHighlightedQuote(null); // Clear highlight
        }
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [highlightedQuote, message.id, setHighlightedQuote]);

  // Import AskKyvaButton dynamically or directly if available
  // Importing directly at top of file is better, but doing it here to minimize diff context issues
  // Note: Ensure to import AskKyvaButton at the top. I'll add the import via another edit if needed, 
  // but for now let's assume I can add it here or use a lazy import if I can't touch top.
  // Actually, I can replace the whole file content or a large chunk to include imports.
  // Let's rely on adding the import statement in a separate small edit or including it if I'm replacing the top.
  // Wait, I am restricted to contiguous edits. I'll just add logic here and users might need to add import manually?
  // No, I should do it properly. I will use the "add import" pattern if this tool allows, but "replace_file_content" is single block.
  // I will assume I need to handle imports separately or try to include them if they are close.
  // But imports are at line 1.
  // I will check if I can just modify the props interface and the function body. 
  // I'll add the button logic here.

  useEffect(() => {
    const handleMouseUp = (e: MouseEvent) => {
      // Ignore interactions with the Ask Kyva button itself
      const target = e.target as HTMLElement;
      if (target.closest('button')) return;

      const selection = window.getSelection();

      // Basic validation
      if (!selection || selection.rangeCount === 0 || selection.isCollapsed) {
        // Don't clear here on mouseup potentially, or maybe we should?
        // If they just clicked (collapsed), we should clear.
        setButtonPosition(null);
        setSelectedText(null);
        return;
      }

      const text = selection.toString().trim();
      if (!text) {
        setButtonPosition(null);
        setSelectedText(null);
        return;
      }

      // Check if selection is inside THIS message
      if (contentRef.current && contentRef.current.contains(selection.anchorNode)) {
        const range = selection.getRangeAt(0);
        const rect = range.getBoundingClientRect();

        // Calculate position relative to viewport
        setButtonPosition({
          x: rect.left + rect.width / 2,
          y: rect.top
        });
        setSelectedText(text);
      } else {
        // If selection is elsewhere, clear this message's button
        if (buttonPosition !== null) {
          setButtonPosition(null);
          setSelectedText(null);
        }
      }
    };

    const handleMouseDown = (e: MouseEvent) => {
      // Clear button when starting a new click/selection, unless clicking the button itself
      // We rely on the button's own click handler to process the action before this clears?
      // Actually click event happens after mousedown/mouseup.
      // If we clear on mousedown, the button click might not register if the button is removed from DOM.
      // So we should check if target is the button.
      const target = e.target as HTMLElement;
      if (target.closest('button')) return;

      setButtonPosition(null);
      setSelectedText(null);
    };

    document.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('mousedown', handleMouseDown);

    const handleScroll = () => {
      if (buttonPosition) setButtonPosition(null);
    };
    window.addEventListener('scroll', handleScroll, true);

    return () => {
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('mousedown', handleMouseDown);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [buttonPosition, message.id]);

  const [isFullScreen, setIsFullScreen] = useState(false);

  const { openFeedbackModal } = useFeedback();

  const isMemoryToolsError = (content: string): boolean => {
    if (!content) return false;
    const lowerContent = content.toLowerCase();
    return (
      (lowerContent.includes("trouble accessing my memory tools") ||
        lowerContent.includes("contact support")) &&
      message.role === "assistant"
    );
  };

  const auth = cognitoUseAuth();

  const handleSupportClick = useCallback(async () => {
    const errorDetails = detectError([...history, message]);

    const issueData: IssueData = {
      issueType: errorDetails ? errorDetails.type : "General Feedback",
      client: "Kyva Web",
      userEmail: auth?.user?.profile?.email || userDisplayName || "Unknown",
      conversationId: sessionId,
      model: selectedModel?.display_name || selectedModel?.name || "Unknown",
      timestamp: new Date().toISOString(),
      lastUserPrompt: history.length > 0 ? history[history.length - 1].content?.parts || "" : "",
      modelError: message.content?.parts || "",
      conversationSummary: history.slice(-4).map(h => `${h.role === 'user' ? 'USER' : 'MODEL'}: ${h.content?.parts || ""}`).join('\n'),
      browser: typeof navigator !== 'undefined' ? navigator.userAgent : "Unknown",
      url: typeof window !== 'undefined' ? window.location.href : "Unknown"
    };

    if (errorDetails) {
      toast.info("Analyzing context to generate issue summary...");
      try {
        const conversationContext = history.slice(-5).map(h => `${h.role}: ${h.content?.parts || ""}`).join('\n') + `\nassistant: ${message.content?.parts || ""}`;
        const summary = await generateFeedbackSummary(conversationContext);

        if (summary && summary.subject && summary.description) {
          openFeedbackModal({
            initialSubject: summary.subject,
            initialDescription: summary.description + "\n\nPlease check this issue.",
            hiddenMetadata: formatSupportEmail(issueData)
          });
        } else {
          // Fallback if AI fails
          openFeedbackModal({
            initialSubject: `Error: ${errorDetails.type}`.slice(0, 50),
            initialDescription: `${errorDetails.message}\n\nPlease check this issue.`,
            hiddenMetadata: formatSupportEmail(issueData)
          });
        }
      } catch (err) {
        // Fallback if network fails
        openFeedbackModal({
          initialSubject: `Error: ${errorDetails.type}`.slice(0, 50),
          initialDescription: `${errorDetails.message}\n\nPlease check this issue.`,
          hiddenMetadata: formatSupportEmail(issueData)
        });
      }
    } else {
      openFeedbackModal({
        hiddenMetadata: formatSupportEmail(issueData)
      });
    }
  }, [sessionId, message, history, userDisplayName, auth?.user, openFeedbackModal]);



  const iframeContainerRef = useRef<HTMLDivElement | null>(null);

  // Audio playback state
  const audioUrl = message.content?.meta?.audio_url;
  const [isPlayingAudio, setIsPlayingAudio] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    if (audioUrl) {
      audioRef.current = new Audio(audioUrl);
      audioRef.current.onended = () => {
        console.log(`⏹️ Message audio ended: message_id=${message.id}`);
        setIsPlayingAudio(false);
      };
      audioRef.current.onpause = () => {
        console.log(`⏸️ Paused message audio: message_id=${message.id}`);
        setIsPlayingAudio(false);
      };
      audioRef.current.onplay = () => {
        console.log(`🎧 Playing message audio: message_id=${message.id}, url=${audioUrl}`);
        setIsPlayingAudio(true);
      };

      // Error handling
      audioRef.current.onerror = (e) => {
        console.error("Audio playback error:", e);
        setIsPlayingAudio(false);
        toast.error("Failed to play audio message");
      };
    }
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, [audioUrl, message.id]);

  const [activeFile, setActiveFile] = useState<{
    fileKey: string;
    fileKind: string;
    indexName?: string;
  } | null>(null);
  const [showFileOptions, setShowFileOptions] = useState(false);
  const [fileOptionsPosition, setFileOptionsPosition] = useState({ x: 0, y: 0 });
  const [showPreview, setShowPreview] = useState(false);

  // environment detection 
  const { authMode } = useAuth();
  // ... (rest of the file)
  // Note: I need to insert imports. Since replace_file_content is contiguous, I cannot easily insert at top AND modify body unless I replace everything between.
  // Strategy: I will replace the component definition to include logic, and separately add import.
  // Or I can assume imports work if I used "ask_kyva_button" logic with fully qualified name if it was a module... no.
  // I will start by adding the logic and ref.



  //link click handler function
  //link click handler function
  const handleLinkClick = useCallback((url: string, e: React.MouseEvent) => {
    e.preventDefault();

    if (authMode === "teams") {
      // Teams environment - show notification
      toast.error("Cannot open new tab in Teams environment. Please use Cognito to open this link.", {
        duration: 4000,
        style: {
          background: '#f7f7f7ff',
          color: '#7F22FE',
        },
      });
    } else {
      // Cognito environment - open in new tab
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  }, [authMode]);

  // Helper function to detect regular URLs
  const isRegularUrl = (href: string | undefined): boolean => {
    if (!href) return false;
    return (
      (href.startsWith('http://') ||
        href.startsWith('https://') ||
        href.startsWith('www.')) &&
      !href.includes('data-file-kind')
    );
  };


  // Copy event handler - removes boxes only when pasting to Word
  useEffect(() => {
    const handleCopy = (e: ClipboardEvent) => {
      const target = e.target as HTMLElement;

      // Only handle copy events within the markdown content
      if (target.closest('.prose')) {
        const selection = window.getSelection();
        if (!selection || selection.toString().length === 0) return;

        // Clone the selected content
        const range = selection.getRangeAt(0);
        const container = document.createElement('div');
        container.appendChild(range.cloneContents());

        // Remove border styles from the cloned content
        const allElements = container.querySelectorAll('*');
        allElements.forEach((element) => {
          if (element instanceof HTMLElement) {
            // Remove border-related inline styles
            element.style.border = 'none';
            element.style.borderWidth = '0';
            element.style.borderStyle = 'none';
            element.style.borderColor = 'transparent';
            element.style.outline = 'none';
            element.style.boxShadow = 'none';

            // Remove border-related CSS classes
            if (element.className) {
              element.className = element.className
                .split(' ')
                .filter(cls => !cls.includes('border'))
                .join(' ');
            }
          }
        });

        // Get the cleaned HTML and plain text
        const cleanHtml = container.innerHTML;
        const plainText = container.textContent || '';

        // Set clipboard data
        e.clipboardData?.setData('text/html', cleanHtml);
        e.clipboardData?.setData('text/plain', plainText);
        e.preventDefault();
      }
    };

    document.addEventListener('copy', handleCopy);
    return () => document.removeEventListener('copy', handleCopy);
  }, []);

  //useEffect to notify parent when preview state changes
  useEffect(() => {
    onFilePreviewStateChange?.(showPreview);
  }, [showPreview, onFilePreviewStateChange]);


  // function to handle direct download
  const handleDirectDownload = async () => {
    if (!activeFile) return;

    try {
      setIsDownloading(true);

      // Get the download URL from the API
      const { presigned_url, display_file_name, file_name } = await getSourceDownloadUrl(
        activeFile.fileKind.toLowerCase(),
        activeFile.fileKey,
        sessionId,
        activeFile.indexName
      );

      // Download the file using the presigned URL
      const response = await fetch(presigned_url);
      if (!response.ok) {
        throw new Error(`Failed to download file: ${response.statusText}`);
      }

      // Name the saved file with the user's ORIGINAL filename (blob downloads
      // discard Content-Disposition, so the backend echoes it in the response).
      // If the backend could not resolve it, fall back to this conversation's own
      // meta (which carries the original name, extension included) before the
      // stored name. Collapse any doubled trailing extension (e.g. legacy
      // Google-Drive "report.pdf.pdf") so the saved file gets a clean name.
      const savedName = dedupeTrailingExt(
        display_file_name ||
        originalNameByStoredName.get(activeFile.fileKey) ||
        file_name ||
        activeFile.fileKey
      );

      // Create a download link and trigger it
      const blob = await response.blob();
      const downloadLink = document.createElement("a");
      const url = window.URL.createObjectURL(blob);
      downloadLink.href = url;
      downloadLink.download = savedName;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
      window.URL.revokeObjectURL(url);

      toast.success(`${savedName} downloaded successfully!`);
    } catch (error) {
      console.error("Error during file download:", error);
      toast.error(`Failed to download file: ${activeFile.fileKey}`);
    } finally {
      setIsDownloading(false);
    }
  };

  const toggleFullScreen = () => {
    if (iframeRef.current) {
      if (!document.fullscreenElement) {
        iframeRef.current.requestFullscreen().catch(err => {
          console.error(`Error attempting to enable full-screen mode: ${err.message} (${err.name})`);
        }).then(() => {
        });
      } else {
        document.exitFullscreen().catch(err => {
          console.error(`Error attempting to disable full-screen mode: ${err.message} (${err.name})`);
        }).then(() => {
        });
      }
    }
    else {
    }
  };

  // Session (non-BrainBank) uploads are stored under a minted, ASCII-safe name, so
  // a `[Source]` citation echoes that name — the user's real filename (special
  // characters / unicode / emoji) survives only in the message meta's
  // `original_file_name`. Build a stable key for those pairs first: a fresh array
  // on every streaming token would re-run the transform effect below for EVERY
  // message, so the map is rebuilt only when the pairs themselves change.
  const filePairsKey = useMemo(() => {
    // Only an assistant message that actually cites something needs the map: a
    // user message renders its chips straight from meta, and skipping the rest
    // keeps this off the hot path while a long conversation streams.
    if (message.role !== "assistant") return "[]";
    if (!(message.content?.parts || "").includes("[Source]")) return "[]";
    const pairs: [string, string][] = [];
    [...(history || []), message].forEach((m: Message) => {
      (m?.content?.meta?.files || []).forEach((f: any) => {
        if (f?.file_name && f?.original_file_name)
          pairs.push([f.file_name, f.original_file_name]);
      });
    });
    return JSON.stringify(pairs);
  }, [history, message, message.role, message.content]);

  // stored name (and its extension-less Bedrock-label form, which is what a
  // citation usually carries) -> the user's original filename. Also feeds the
  // shared resolver, so the citation label, the preview header and the downloaded
  // file all show the real name — including after a reload, when the upload-time
  // map is empty. Registration is idempotent.
  const originalNameByStoredName = useMemo(() => {
    const map = new Map<string, string>();
    (JSON.parse(filePairsKey) as [string, string][]).forEach(([stored, original]) => {
      registerOriginalName(stored, original);
      const display = dedupeTrailingExt(original);
      map.set(stored, display);
      const dot = stored.lastIndexOf(".");
      if (dot > 0) map.set(stored.slice(0, dot), display);
    });
    return map;
  }, [filePairsKey]);

  // Resolve brainbank source citations to the user's real filename. Scans this
  // message for `src/brainbank/{index}/{file}` citations, fetches each index's
  // stored→original map once, and bumps nameResolveVersion so the transform
  // effect re-runs and getDisplayFileName shows the real (emoji/unicode) name.
  useEffect(() => {
    if (message.role !== "assistant") return;
    const content = message.content?.parts || "";

    const brainbankSrcRegex = /\[Source\]:\s*src\/brainbank\/([^\]\n[]+)/g;
    const indexes = new Set<string>();
    let m: RegExpExecArray | null;
    while ((m = brainbankSrcRegex.exec(content)) !== null) {
      const indexName = m[1].split("/")[0];
      if (indexName) indexes.add(indexName);
    }
    if (indexes.size === 0) return;

    let cancelled = false;
    Promise.all([...indexes].map(ensureBrainbankSourceNames)).then(() => {
      if (!cancelled) setNameResolveVersion((v) => v + 1);
    });
    return () => {
      cancelled = true;
    };
  }, [message.content, message.role]);

  useEffect(() => {
    const sourceRegex = /\[Source\]:\s*(?:src\/(conversation|brainbank)\/([^\]\n[]+)|<a\s+[^>]*data-file-kind="([^"]+)"[^>]*data-file-key="([^"]+)"(?:[^>]*data-index-name="([^"]+)")?[^>]*>([^<]+)<\/a>)/g;

    const messageContent = message.content?.parts || "";

    if (message.role === "assistant") {
      const thinkStartTag = '<think>';
      const thinkEndTag = '</think>';
      const thinkStartIndex = messageContent.indexOf(thinkStartTag);
      const thinkEndIndex = messageContent.indexOf(thinkEndTag);

      let extractedThinking = '';
      let processedMessage = messageContent;

      if (thinkStartIndex !== -1) {
        if (thinkEndIndex !== -1) {
          extractedThinking = messageContent.slice(
            thinkStartIndex + thinkStartTag.length,
            thinkEndIndex
          ).trim();
          processedMessage = messageContent.slice(0, thinkStartIndex) +
            messageContent.slice(thinkEndIndex + thinkEndTag.length);
        } else {
          extractedThinking = messageContent.slice(thinkStartIndex + thinkStartTag.length);
          processedMessage = messageContent.slice(0, thinkStartIndex);
        }
      }

      setThinkingContent(extractedThinking);

      // Replace <document> tags with unique placeholders to preserve them
      // through the rest of the processing pipeline
      const docContents: string[] = [];
      const DOC_PLACEHOLDER = '__DOCUMENT_BLOCK_';
      processedMessage = processedMessage.replace(
        /<document>([\s\S]*?)<\/document>/g,
        (_, content) => {
          docContents.push(content.trim());
          return `${DOC_PLACEHOLDER}${docContents.length - 1}__`;
        }
      );
      // Handle streaming: unclosed <document> tag (content still arriving)
      const isStreamingDoc = processedMessage.includes('<document>') && !processedMessage.includes('</document>');
      if (isStreamingDoc) {
        processedMessage = processedMessage.replace(
          /<document>([\s\S]*)$/,
          (_, content) => {
            docContents.push(content);
            return `${DOC_PLACEHOLDER}${docContents.length - 1}__`;
          }
        );
      }

      // Replace <spreadsheet> tags with unique placeholders
      const sheetContents: string[] = [];
      const SHEET_PLACEHOLDER = '__SPREADSHEET_BLOCK_';
      processedMessage = processedMessage.replace(
        /<spreadsheet>([\s\S]*?)<\/spreadsheet>/g,
        (_, content) => {
          sheetContents.push(content.trim());
          return `${SHEET_PLACEHOLDER}${sheetContents.length - 1}__`;
        }
      );
      // Handle streaming: unclosed <spreadsheet> tag (content still arriving)
      const isStreamingSheet = processedMessage.includes('<spreadsheet>') && !processedMessage.includes('</spreadsheet>');
      if (isStreamingSheet) {
        processedMessage = processedMessage.replace(
          /<spreadsheet>([\s\S]*)$/,
          (_, content) => {
            sheetContents.push(content);
            return `${SHEET_PLACEHOLDER}${sheetContents.length - 1}__`;
          }
        );
      }

      // Replace <presentation> tags with unique placeholders
      const presContents: string[] = [];
      const PRES_PLACEHOLDER = '__PRESENTATION_BLOCK_';
      processedMessage = processedMessage.replace(
        /<presentation>([\s\S]*?)<\/presentation>/g,
        (_, content) => {
          presContents.push(content.trim());
          return `${PRES_PLACEHOLDER}${presContents.length - 1}__`;
        }
      );
      // Handle streaming: unclosed <presentation> tag (content still arriving)
      const isStreamingPres = processedMessage.includes('<presentation>') && !processedMessage.includes('</presentation>');
      if (isStreamingPres) {
        processedMessage = processedMessage.replace(
          /<presentation>([\s\S]*)$/,
          (_, content) => {
            presContents.push(content);
            return `${PRES_PLACEHOLDER}${presContents.length - 1}__`;
          }
        );
      }

      // Remove standalone JSON responses (not in code blocks)
      const codeBlockRegex = /```[\s\S]*?```/g;
      const codeBlocks: string[] = [];
      let tempMessage = processedMessage.replace(codeBlockRegex, (match) => {
        codeBlocks.push(match);
        return `__CODE_BLOCK_${codeBlocks.length - 1}__`;
      });

      // Only remove specific raw JSON patterns from tool responses
      // Pattern 1: Complete tool response JSON objects
      tempMessage = tempMessage.replace(/^\s*\{[\s\S]*?"(?:text|status_code|url|content_type)"[\s\S]*?\}\s*$/gm, '');

      // Pattern 2: Success/data wrapper JSON
      tempMessage = tempMessage.replace(/^\s*\{"success":\s*(?:true|false)[\s\S]*?\}\s*$/gm, '');

      // Pattern 3: Standalone JSON objects with common API response fields
      tempMessage = tempMessage.replace(/^\s*\{[\s\S]*?"(?:response_time|result_count|query|results)"[\s\S]*?\}\s*$/gm, '');

      // Restore code blocks (including tool_call and tool_response)
      codeBlocks.forEach((block, index) => {
        tempMessage = tempMessage.replace(`__CODE_BLOCK_${index}__`, block);
      });

      processedMessage = tempMessage;

      // Hoist all tool_call / tool_response code blocks to the top of the
      // message so the ToolOperationDisplay banner always renders BEFORE the
      // assistant's text. Without this, when the LLM interleaves tool calls
      // between text paragraphs (which it freely does in multi-step
      // workflows), the banner appears mid-message at the position of the
      // last tool block. Hoisting guarantees a single banner at the top
      // regardless of LLM tool-use timing.
      {
        const toolBlockRegex = /```tool_(?:call|response)\n[\s\S]*?\n```/g;
        const toolBlocks: string[] = [];
        let textOnlyMessage = processedMessage.replace(toolBlockRegex, (match) => {
          toolBlocks.push(match);
          return '';
        });
        if (toolBlocks.length > 0) {
          textOnlyMessage = textOnlyMessage.replace(/\n{3,}/g, '\n\n').trim();
          processedMessage = toolBlocks.join('\n\n') + (textOnlyMessage ? '\n\n' + textOnlyMessage : '');
        }
      }

      // Clean up any leftover empty lines
      processedMessage = processedMessage.replace(/\n{3,}/g, '\n\n');

      const transformedMessage = processedMessage.replace(
        sourceRegex,
        (match, srcKind, srcPath, htmlKind, htmlFileKey, htmlIndexName, htmlDisplayText) => {
          let fileKey = "";
          let kind = "";
          let indexName = "";

          if (srcKind && srcPath) {
            kind = srcKind;
            if (srcKind === "brainbank") {
              const parts = srcPath.split("/");
              indexName = parts[0];
              fileKey = parts.slice(1).join("/");
            } else {
              fileKey = srcPath;
            }
          }
          else if (htmlKind && htmlFileKey) {
            kind = htmlKind;
            fileKey = htmlFileKey;
            indexName = htmlIndexName || "";
          }
          else {
            return match;
          }

          // The cited path is captured up to the next "[" (the [Page] tag), so it
          // carries the space that separates them. Trim it: an untrimmed key
          // matches no stored→original entry, which left the minted storage name
          // on screen instead of the user's real filename.
          fileKey = fileKey.trim();
          indexName = indexName.trim();
          if (!fileKey) return match;

          // data-file-key keeps the cited stored name (the backend resolves it);
          // the visible label hides the minted "-8hex" upload suffix.
          return `[Source]: <a href="" target="_blank" data-file-kind="${kind}" data-file-key="${fileKey}"${indexName ? ` data-index-name="${indexName}"` : ""
            }>${getDisplayFileName(fileKey)}</a>`;
        }
      );

      let finalMessage = transformedMessage;

      // A citation the model emits WITHOUT the "src/<kind>/" prefix stays plain
      // text above, so it would still show the minted storage name. Swap in the
      // user's original name, but only when the cited string is a file this
      // conversation actually uploaded — every other "[Source]:" line is passed
      // through untouched. The character class stops at "[" (the following [Page]
      // tag) and cannot match the "<a>" form produced above, so links are safe.
      if (originalNameByStoredName.size > 0) {
        finalMessage = finalMessage.replace(
          /(\[Source\]:[ \t]*)([^[\n<]+)/g,
          (match, prefix, cited) => {
            const original = originalNameByStoredName.get(cited.trim());
            return original ? `${prefix}${cited.replace(cited.trim(), () => original)}` : match;
          }
        );
      }

      if (isMemoryToolsError(finalMessage)) {
        finalMessage = finalMessage.replace(
          /contact support/gi,
          '[contact support](#contact-support)'
        );
      }

      if (highlightedQuote && highlightedQuote.messageId === message.id) {
        const safeQuote = highlightedQuote.text
          .replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
          .replace(/\s+/g, '\\s+');

        finalMessage = finalMessage.replace(
          new RegExp(`(${safeQuote})`, 'gi'),
          '<mark class="bg-violet-200 rounded-sm">$1</mark>'
        );
      }

      setParsedMessage(finalMessage);

      // Split finalMessage into text/document/spreadsheet/presentation segments for rendering
      const placeholderRegex = /__(?:DOCUMENT_BLOCK|SPREADSHEET_BLOCK|PRESENTATION_BLOCK)_(\d+)__/g;
      const segments: { type: 'text' | 'document' | 'spreadsheet' | 'presentation'; content: string }[] = [];
      let segLastIndex = 0;
      let segMatch: RegExpExecArray | null;

      while ((segMatch = placeholderRegex.exec(finalMessage)) !== null) {
        if (segMatch.index > segLastIndex) {
          segments.push({ type: 'text', content: finalMessage.slice(segLastIndex, segMatch.index) });
        }
        const fullMatch = segMatch[0];
        if (fullMatch.startsWith('__DOCUMENT_BLOCK_')) {
          segments.push({ type: 'document', content: docContents[parseInt(segMatch[1], 10)] });
        } else if (fullMatch.startsWith('__SPREADSHEET_BLOCK_')) {
          segments.push({ type: 'spreadsheet', content: sheetContents[parseInt(segMatch[1], 10)] });
        } else {
          segments.push({ type: 'presentation', content: presContents[parseInt(segMatch[1], 10)] });
        }
        segLastIndex = segMatch.index + fullMatch.length;
      }

      if (segLastIndex < finalMessage.length) {
        segments.push({ type: 'text', content: finalMessage.slice(segLastIndex) });
      }

      if (segments.length === 0) {
        segments.push({ type: 'text', content: finalMessage });
      }

      setDocumentSegments(segments);

      const speechText = transformedMessage
        .replace(/\[Page\]:\s*[\d\-,\s]+/g, '')
        .replace(/\[BrainBank\]:\s*[^\s[\]]+/g, '')
        .replace(/<a[^>]*>([^<]+)<\/a>/g, '$1')
        .replace(/\[Source\]:\s*/g, 'Source: ');

      setSpeechMessage(speechText);
    } else {
      setSpeechMessage(messageContent);
      setParsedMessage(messageContent);
      setDocumentSegments([{ type: 'text', content: messageContent }]);
    }
  }, [message.content, message.role, highlightedQuote, message.id, nameResolveVersion, originalNameByStoredName]);

  const { speechStatus, start, stop } = useSpeech({ text: speechMessage });

  const handleCopyCode = (code: string) => {
    navigator.clipboard.writeText(code).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };





  const generateDiagramId = () => {
    return `mermaid-${uuidv4()}`;
  };

  const [isIframeView, setIsIframeView] = useState(false);

  const markdownComponents = useMemo(() => ({
    h1: ({ node, children, ...props }: any) => (
      <h1
        className="text-2xl font-bold mt-6 mb-3"
        {...props}
      >
        {children}
      </h1>
    ),
    h2: ({ node, children, ...props }: any) => (
      <h2
        className="text-xl font-semibold mt-4 mb-2"
        {...props}
      >
        {children}
      </h2>
    ),
    h3: ({ node, children, ...props }: any) => (
      <h3
        className="text-lg font-bold mt-3 mb-1.5"
        {...props}
      >
        {children}
      </h3>
    ),
    ol: ({ node, ...props }: any) => (
      <ol
        className="list-decimal list-outside mb-4 pl-8"
        {...props}
      />
    ),
    ul: ({ node, ...props }: any) => (
      <ul
        className="list-disc list-outside mb-4 pl-8"
        {...props}
      />
    ),
    li: ({ node, ...props }: any) => (
      <li className="mb-2 pl-1" {...props} />
    ),
    code: ({
      node,
      inline,
      className,
      children,
      ...props
    }: any) => {
      const match = /language-(\w+)/.exec(className || "");
      if (!children || children.toString().trim() === "") {
        return null;
      }
      if (!inline && match) {
        if (match[1] === "tool_call") {
          return (
            <ToolOperationDisplay
              type="tool_call"
              content={String(children).replace(/\n$/, "")}
            />
          );
        }
        if (match[1] === "tool_response") {
          return (
            <ToolOperationDisplay
              type="tool_response"
              content={String(children).replace(/\n$/, "")}
            />
          );
        }
        if (match[1] === "html") {
          return (
            <div className="w-full text-xs lg:text-sm rounded-lg my-2 overflow-x-auto">
              <div style={{ borderRadius: "4px", overflow: "hidden" }} ref={iframeRef}>
                <div className="bg-white rounded-md border-[0.5px] border-token-border-medium overflow-visible">
                  <div className="flex items-center relative text-token-text-secondary bg-token-main-surface-secondary px-4 py-2 text-xs font-sans justify-between rounded-t-md">
                    <span>{match[1]}</span>
                    <div className="flex items-center space-x-2">
                      {!isLast && (
                        <button
                          className="flex gap-1 items-center"
                          onClick={() => setIsIframeView((prev) => !prev)}
                        ><Album size={12} />
                          {isIframeView ? "View Code" : "View Render"}
                        </button>
                      )}

                      {!isLast && isIframeView && (
                        <button
                          onClick={() => { toggleFullScreen(); }}
                          className="flex gap-1 items-center"
                        ><Fullscreen size={12} />
                          Fullscreen
                        </button>
                      )}

                      {!loading && isLast && (
                        <button
                          className="flex gap-1 items-center"
                          onClick={() => setIsIframeView((prev) => !prev)}
                        ><Album size={12} />
                          {isIframeView ? "View Code" : "View Render"}
                        </button>
                      )}

                      {!loading && isLast && isIframeView && (
                        <button
                          onClick={() => { toggleFullScreen(); }}
                          className="flex gap-1 items-center"
                        ><Fullscreen size={12} />
                          Fullscreen
                        </button>
                      )}

                      <button
                        className="flex gap-1 items-center"
                        onClick={() =>
                          handleCopyCode(String(children).replace(/\n$/, ""))
                        }
                      >
                        {copied ? (
                          <>
                            <CopyCheck size={12} />
                            Copied!
                          </>
                        ) : (
                          <>
                            <CopyIcon size={12} />
                            Copy
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                  <div className="overflow-y-auto p-4" dir="ltr">
                    {isIframeView ? (
                      <div style={{
                        backgroundColor: "#ffffff",
                        height: "90dvh",
                      }}

                      >
                        <iframe
                          title="HTML Preview"
                          srcDoc={String(children).replace(/\n$/, "")}
                          style={{
                            width: "100%",
                            height: "100%",
                            border: "1px solid #ddd",
                            borderRadius: "8px",
                            display: "block",
                          }}
                        />
                      </div>
                    ) : (
                      <SyntaxHighlighter
                        language={match[1]}
                        style={tomorrow}
                        customStyle={{
                          padding: "1rem",
                          borderRadius: "8px",
                          maxHeight: "500px",
                          overflow: "auto",
                          display: "flex",
                          flexDirection: "column-reverse"
                        }}
                        {...props}
                      >
                        {String(children).replace(/\n$/, "")}
                      </SyntaxHighlighter>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        }
        if (match[1] === "mermaid" && !isLast) {
          return (
            <MermaidRender
              chart={children}
              diagramId={generateDiagramId()}
            />
          );
        }
        if (match[1] === "mermaid" && isLast) {
          return (
            <MermaidRenderLast
              chart={children}
              diagramId={generateDiagramId()}
              isLoading={loading}
              isLast={isLast}
            />
          );
        }
        if (match[1] === "markdown" && isLast) {
          return <Documentation children={children} />;
        }
        return (
          <div className="w-full text-xs lg:text-sm rounded-lg my-2 overflow-hidden" style={{ display: "grid" }}>
            <div
              style={{
                borderRadius: "4px",
                overflow: "hidden",
              }}
            >
              <div className=" bg-white rounded-md border-[0.5px] border-token-border-medium">
                <div className="flex items-center relative text-token-text-secondary bg-token-main-surface-secondary px-4 py-2 text-xs font-sans justify-between rounded-t-md overflow-visible">
                  <span>{match[1]}</span>
                  <div className="flex items-center">
                    <span className="" data-state="closed">
                      <button
                        className="flex gap-1 items-center"
                        onClick={() =>
                          handleCopyCode(
                            String(children).replace(
                              /\n$/,
                              ""
                            )
                          )
                        }
                      >
                        {copied ? (
                          <>
                            <CopyCheck size={12} />
                            Copied!
                          </>
                        ) : (
                          <>
                            <CopyIcon size={12} />
                            Copy
                          </>
                        )}
                      </button>
                    </span>
                  </div>
                </div>
                <div
                  className="overflow-y-auto p-4"
                  dir="ltr"
                >
                  <SyntaxHighlighter
                    language={match[1]}
                    style={tomorrow}
                    customStyle={{
                      padding: "1rem",
                      borderRadius: "8px",
                    }}
                    {...props}
                  >
                    {String(children).replace(/\n$/, "")}
                  </SyntaxHighlighter>
                </div>
              </div>
            </div>
          </div>
        );
      }
      else {
        if (inline) {
          return (
            <code className="bg-gray-100 text-gray-800 rounded px-1.5 py-0.5 text-[0.85em] font-mono break-words" {...props}>
              {children}
            </code>
          );
        }
        return (
          <div className="w-full text-xs lg:text-sm rounded-lg my-2 overflow-hidden" style={{ display: "grid" }}>
            <div style={{ borderRadius: "4px", overflow: "hidden" }}>
              <div className="bg-white rounded-md border-[0.5px] border-token-border-medium">
                <div className="flex items-center relative text-token-text-secondary bg-token-main-surface-secondary px-4 py-2 text-xs font-sans justify-between rounded-t-md overflow-visible">
                  <span>code</span>
                  <div className="flex items-center">
                    <button
                      className="flex gap-1 items-center"
                      onClick={() =>
                        handleCopyCode(String(children).replace(/\n$/, ""))
                      }
                    >
                      {copied ? (
                        <>
                          <CopyCheck size={12} />
                          Copied!
                        </>
                      ) : (
                        <>
                          <CopyIcon size={12} />
                          Copy
                        </>
                      )}
                    </button>
                  </div>
                </div>
                <div className="overflow-y-auto p-4" dir="ltr">
                  <SyntaxHighlighter
                    language="text"
                    style={tomorrow}
                    customStyle={{
                      padding: "1rem",
                      borderRadius: "8px",
                    }}
                    {...props}
                  >
                    {String(children).replace(/\n$/, "")}
                  </SyntaxHighlighter>
                </div>
              </div>
            </div>
          </div>
        );
      }
    },
    strong: ({ node, ...props }: any) => (
      <strong className="font-bold" {...props} />
    ),
    p: ({ node, ...props }: any) => (
      <p className="mb-4" {...props} />
    ),
    a: ({ node, href, children, ...props }: any) => {
      if (href === "#contact-support") {
        return (
          <a
            href="#"
            className="text-violet-600 hover:text-violet-800 underline hover:no-underline font-medium transition-colors cursor-pointer"
            onClick={(e) => {
              e.preventDefault();
              handleSupportClick();
            }}
          >
            {children}
          </a>
        );
      }

      if (isRegularUrl(href)) {
        const isUrlCopied = copiedUrl === href;
        return (
          <span className="relative inline-flex items-center group/link">
            <a
              href={href}
              className="text-violet-500 hover:text-violet-700 underline hover:no-underline transition-colors duration-200 cursor-pointer"
              onClick={(e) => handleLinkClick(href!, e)}
              {...props}
            >
              {children}
            </a>
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                navigator.clipboard.writeText(href!).then(() => {
                  setCopiedUrl(href!);
                  setTimeout(() => setCopiedUrl(null), 2000);
                }).catch(err => {
                  console.error("Failed to copy link", err);
                });
              }}
              className="ml-1 p-0.5 rounded opacity-0 group-hover/link:opacity-100 focus:opacity-100 transition-opacity duration-200 text-gray-400 hover:text-violet-600 align-middle"
              title={isUrlCopied ? "Copied!" : "Copy link"}
              aria-label="Copy link"
            >
              {isUrlCopied ? <CopyCheck size={12} /> : <CopyIcon size={12} />}
            </button>
          </span>
        );
      }

      return (
        <div className="relative inline-block group">
          <span className="relative z-10 transition-colors duration-300 text-violet-500 group-hover:text-white underline hover:no-underline">
            <a
              className="px-1"
              {...props}
              onClick={(e) => {
                e.preventDefault();
                const fileKey = (props as { "data-file-key": string })["data-file-key"];
                const fileKind = (props as { "data-file-kind": string })["data-file-kind"];
                const indexName = (props as { "data-index-name": string })["data-index-name"];

                if (fileKind && fileKey) {
                  setActiveFile({ fileKey, fileKind, indexName });

                  setFileOptionsPosition({
                    x: e.clientX,
                    y: e.clientY
                  });
                  setShowFileOptions(true);
                }
              }}
            >
              {children}
            </a>
            {isDownloading && (
              <LoaderCircle className="inline-block w-4 h-4 mx-1 mb-1 animate-spin" />
            )}
          </span>
          <span className="absolute inset-x-0 bottom-0 h-0 bg-violet-500 transition-all duration-300 ease-out group-hover:h-full rounded"></span>
        </div>
      );
    },
    blockquote: ({ node, ...props }: any) => (
      <blockquote
        className="border-l-4 border-gray-300 pl-4 italic my-4"
        {...props}
      />
    ),
    table: ({ node, ...props }: any) => (
      <div className="relative pt-8 pb-2 w-full">
        <div className="absolute right-0 top-0 z-10">
          <button
            onClick={(e) => {
              const button = e.currentTarget;
              if (!button) return;

              const buttonParent = button.closest('div');
              if (!buttonParent) return;

              const tableContainer = buttonParent.parentElement;
              if (!tableContainer) return;

              const tableElement = tableContainer.querySelector('table');
              if (!tableElement) return;

              const tableText = tableElement.innerText;
              navigator.clipboard.writeText(tableText).then(() => {
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
              }).catch(err => {
                console.error("Failed to copy table content", err);
              });
            }}
            className="flex gap-1 items-center bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold py-1 px-3 rounded text-xs"
          >
            {copied ? (
              <>
                <CopyCheck size={12} />
                Copied!
              </>
            ) : (
              <>
                <CopyIcon size={12} />
                Copy
              </>
            )}
          </button>
        </div>
        {/* overflow-x-auto enables horizontal scrolling; the table uses
            w-auto + min-w-full so it grows to its natural width instead of
            squishing many columns into a single character per line */}
        <div className="overflow-x-auto w-full max-w-full rounded-xl border border-gray-200 shadow-sm">
          <table
            className="border-collapse w-auto min-w-full text-sm text-left"
            {...props}
          />
        </div>
      </div>
    ),
    thead: ({ node, ...props }: any) => (
      <thead className="bg-gray-50" {...props} />
    ),
    tr: ({ node, ...props }: any) => (
      <tr
        className="border-b border-gray-100 last:border-0 even:bg-gray-50/40 hover:bg-violet-50/50 transition-colors"
        {...props}
      />
    ),
    th: ({ node, ...props }: any) => (
      <th
        className="whitespace-nowrap px-4 py-2.5 text-left align-top font-semibold text-gray-700 border-b border-gray-200"
        {...props}
      />
    ),
    td: ({ node, ...props }: any) => (
      <td
        className="px-4 py-2.5 align-top text-left min-w-[120px] max-w-[360px] break-words"
        {...props}
      />
    ),
  }), [loading, isLast, copied, copiedUrl, isIframeView, isDownloading, authMode, handleLinkClick]);

  // Shared markdown renderer, reused by WorkflowPipeline so each workflow step's
  // body renders identically to a normal message (same plugins + components).
  const renderMarkdown = useCallback(
    (text: string) => (
      <Markdown
        className="prose break-words dark:prose-invert prose-p:leading-relaxed prose-pre:p-0 max-w-full w-full overflow-hidden"
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeRaw]}
        components={markdownComponents}
      >
        {sanitizeMath(text)}
      </Markdown>
    ),
    [markdownComponents]
  );

  return (
    <div id={`message-${message.id}`}>
      {message.role === "user" && (
        <div
          className={mergeTailwindClasses(
            "w-full",
            messageIndex === 0 ? "pt-16" : "pt-8",
            "flex justify-end"
          )}
        >
          <div className=" text-justify">
            {message.content?.meta?.quotedText && (
              <div className="mb-2 w-full max-w-full">
                {message.content.meta.quotedMessageId ? (
                  <QuotePreview
                    text={message.content.meta.quotedText}
                    onClick={() => {
                      const el = document.getElementById(`message-${message.content.meta.quotedMessageId}`);
                      if (el) {
                        el.scrollIntoView({ behavior: "smooth", block: "center" });
                        // Trigger precise text highlight
                        if (setHighlightedQuote && message.content.meta.quotedMessageId && message.content.meta.quotedText) {
                          setHighlightedQuote({
                            messageId: message.content.meta.quotedMessageId,
                            text: message.content.meta.quotedText
                          });
                        }
                      }
                    }}
                  />
                ) : (
                  <QuoteBlock text={message.content.meta.quotedText} />
                )}
              </div>
            )}

            {message.content?.parts && (
              <div
                className="text-primary font-medium text-md bg-surface-2 p-2 rounded-lg max-w-max text-sm"
                style={{
                  whiteSpace: 'pre-wrap',
                  wordWrap: 'break-word',
                  lineHeight: '1.5'
                }}
              >
                {(() => {
                  let parts = message.content.parts;

                  // 1. Strip context suffix if quotedText exists
                  if (message.content.meta?.quotedText) {
                    const suffix = `\n\n[Context from selected text: "${message.content.meta.quotedText}"]`;
                    if (parts.endsWith(suffix)) {
                      parts = parts.slice(0, -suffix.length);
                    }
                  }

                  // 2. Apply highlighting if this is the target message
                  if (highlightedQuote && highlightedQuote.messageId === message.id) {
                    // Escape special regex characters in the quote text, but allow for flexible whitespace
                    // Replace whitespace in quote with \s+ regex to match any whitespace sequence
                    const safeQuote = highlightedQuote.text
                      .replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
                      .replace(/\s+/g, '\\s+');

                    const partsSplit = parts.split(new RegExp(`(${safeQuote})`, 'g'));

                    return partsSplit.map((part, index) => {
                      // Check if this part matches the quote (ignoring whitespace differences)
                      const isMatch = part.replace(/\s+/g, ' ').trim() === highlightedQuote.text.replace(/\s+/g, ' ').trim();

                      return isMatch ? (
                        <mark key={index} className="bg-violet-200 rounded-sm">{part}</mark>
                      ) : (
                        <span key={index}>{part}</span>
                      );
                    });
                  }

                  return parts;
                })()}
              </div>
            )}

            {message.content?.meta?.files?.length > 0 && (
              <div className="grid grid-cols-1 gap-2 my-2 w-full">
                {message.content.meta.files.map((file, index) => (
                  <div
                    key={index}
                    className="relative flex items-center overflow-hidden rounded-xl border border-violet-300 p-2 bg-white w-full max-w-full min-h-[50px]"
                  >
                    <div>
                      <FileIcon file={file} />
                    </div>
                    <div className="ml-2 flex-grow overflow-hidden">
                      <div className="truncate font-semibold text-xs text-left">
                        {file.original_file_name || file.file_name}
                      </div>
                      <div className="truncate text-xs text-slate-500 text-left">
                        {formatFileSize(file.file_size)}
                      </div>
                      <AttachmentSyncStatus sessionId={sessionId} fileName={file.file_name} />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {message.role === "assistant" && (
        <div className="text-md">
          <div className="flex">
            <div className="pr-2 font-bold">
              <div className="flex items-center">
                <div className="flex items-center">
                  <div className={`relative w-10 h-10`}>
                    <svg className="w-full h-full" viewBox="0 0 100 100">
                      <circle
                        className="text-gray-400 stroke-current"
                        strokeWidth="2"
                        cx="50"
                        cy="50"
                        r="48"
                        fill="transparent"
                      />
                    </svg>
                    <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                      <KyvaIcon width="24" height="24" />
                    </div>
                  </div>
                  <div className="pl-2 pt-1 font-semibold">Kyva</div>
                </div>
                {loading && isLast && (
                  <LoaderCircle
                    className={`ml-2 mt-2 ${loading && isLast ? "animate-spin" : "animate-none"
                      }`}
                    size={20}
                  />
                )}
              </div>
            </div>
          </div>

          {loading && isLast && (
            <div className="flex items-center text-sm pt-6">
              <div className="pr-2 font-semibold text-gray-800 flex items-center">
                <span className="inline-flex mr-2">
                  <AnimatedProgressCircle analyzing={false} />
                </span>
                {"Typing"}
                <span className="inline-flex ml-2">
                  <span className="animate-[ping_1s_ease-in-out_infinite] h-2 w-2 rounded-full bg-gray-500 mr-1"></span>
                  <span className="animate-[ping_1s_ease-in-out_0.333s_infinite] h-2 w-2 rounded-full bg-gray-500 mr-1"></span>
                  <span className="animate-[ping_1s_ease-in-out_0.666s_infinite] h-2 w-2 rounded-full bg-gray-500"></span>
                </span>
              </div>
            </div>
          )}

          <div className="flex flex-col space-y-9 lg:space-y-0 lg:flex-row lg:justify-start lg:space-x-9">
            <div
              ref={dividerRef}
              className="flex flex-col w-full max-w-full overflow-hidden px-2 sm:px-4"
            >
              {thinkingContent && (
                <ThinkingProcess
                  content={thinkingContent}
                  isStreaming={loading && isLast}
                />
              )}
              {message.sources && message.sources.length > 0 && (
                <div className="flex flex-col space-y-2">
                  <div className="flex flex-row items-center space-x-2">
                    <BookCopy className="" size={20} />
                    <h3 className="font-medium text-xl">Sources</h3>
                  </div>
                  <MessageSources sources={message.sources} />
                </div>
              )}
              <div className="flex flex-col space-y-2">
                <div className="flex flex-row items-center space-x-2"></div>
                <div className="relative my-8 flex items-start w-full max-w-full">
                  <div className="flex-1 space-y-2 overflow-hidden w-full select-text" ref={contentRef}>
                    <ToolOperationsProvider>
                    {documentSegments.map((seg, idx) =>
                      seg.type === 'document' ? (
                        <DocumentExport key={`doc-${idx}`} text={seg.content} />
                      ) : seg.type === 'spreadsheet' ? (
                        <SpreadsheetExport key={`sheet-${idx}`} text={seg.content} />
                      ) : seg.type === 'presentation' ? (
                        <PresentationExport key={`pres-${idx}`} text={seg.content} />
                      ) : isWorkflowContent(seg.content) ? (
                        <WorkflowPipeline
                          key={`wf-${idx}`}
                          content={seg.content}
                          loading={loading}
                          isLast={isLast}
                          renderMarkdown={renderMarkdown}
                        />
                      ) : (
                        <Markdown
                          key={`md-${idx}`}
                          className="prose break-words dark:prose-invert prose-p:leading-relaxed prose-pre:p-0 max-w-full w-full overflow-hidden"
                          remarkPlugins={[remarkGfm]}
                          rehypePlugins={[rehypeRaw]}
                          components={markdownComponents}
                        >
                          {sanitizeMath(seg.content)}
                        </Markdown>
                      )
                    )}
                    </ToolOperationsProvider>
                    <AskKyvaButton
                      position={buttonPosition}
                      onClick={() => {
                        if (selectedText && handleQuote) {
                          handleQuote(selectedText, message.id);
                          // Clear selection data after clicking
                          setButtonPosition(null);
                          setSelectedText(null);
                          window.getSelection()?.removeAllRanges();
                        }
                      }}
                    />
                  </div>
                </div>

                {!loading || !isLast ? (
                  <div className="flex flex-row items-center justify-start w-full py-2 -mx-2">
                    <div className="flex flex-row items-center space-x-1">
                      <Copy
                        initialMessage={message.content?.parts || ""}
                        message={message}
                      />
                      <button
                        onClick={() => {
                          if (audioUrl && audioRef.current) {
                            if (isPlayingAudio) {
                              audioRef.current.pause();
                            } else {
                              // Stop TTS if running
                              if (speechStatus === "started") stop();
                              audioRef.current.play().catch(e => console.error("Play error:", e));
                            }
                          } else {
                            if (speechStatus === "started") {
                              stop();
                            } else {
                              start();
                            }
                          }
                        }}
                        className="p-2 rounded-xl text-muted hover:text-primary hover:bg-surface-2 transition-colors duration-200"
                      >
                        {(audioUrl ? isPlayingAudio : speechStatus === "started") ? (
                          <StopCircle size={18} />
                        ) : (
                          <Volume2 size={18} />
                        )}
                      </button>
                      {/* button to prompt user to predefined question like I would like to create Virtual Assistant based on this conversation */}
                      {canCreateVA && (
                        <button
                          onClick={() => {
                            createVirtualAssistant();
                          }}
                          className="p-2 rounded-xl text-muted hover:text-primary bg-surface-2 hover:bg-surface-3 transition-colors duration-200 relative"
                        >
                          <img
                            src='/va-icon-bw.svg'
                            alt="Virtual Assistant Icon"
                            className="w-5 h-5"
                          />
                          <Plus className="w-3 h-3 absolute top-0.5 right-0.5 text-muted stroke-[3]" />
                        </button>
                      )}
                    </div>
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      )}
      {/* File Options Menu */}
      <FileOptionsMenu
        isOpen={showFileOptions}
        onClose={() => setShowFileOptions(false)}
        onPreview={() => {
          setShowFileOptions(false);
          setShowPreview(true);
        }}
        onDownload={handleDirectDownload}
        position={fileOptionsPosition}
      />

      {/* File Preview Modal */}
      <FilePreview
        isOpen={showPreview}
        onClose={() => {
          setShowPreview(false);
          setActiveFile(null);
        }}
        fileInfo={activeFile}
      />
    </div>
  );
}

// -----------------------------------------
// Memoize with a custom areEqual function
// to avoid re-rendering unless props changed
// -----------------------------------------
function areMessagesEqual(prevProps: any, nextProps: any) {
  const isEqual =
    prevProps.message === nextProps.message &&
    prevProps.loading === nextProps.loading &&
    prevProps.isLast === nextProps.isLast &&
    prevProps.highlightedQuote === nextProps.highlightedQuote;

  return isEqual;
}
export default memo(MessageBox, areMessagesEqual);